<template>
  <div class="agent-leads-view">
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <router-link to="/agent-dashboard" class="back-button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
        </router-link>
        <div>
          <h1>My Leads</h1>
          <p class="subtitle">Manage your assigned leads</p>
        </div>
      </div>
      <button class="refresh-button" @click="loadLeads" :disabled="isLoading">
        <svg :class="{ spinning: isLoading }" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
        </svg>
        Refresh
      </button>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filter-group">
        <label>Status</label>
        <select v-model="filters.status" @change="loadLeads">
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="qualified">Qualified</option>
          <option value="proposal">Proposal</option>
          <option value="negotiation">Negotiation</option>
          <option value="won">Won</option>
          <option value="lost">Lost</option>
        </select>
      </div>

      <div class="filter-group">
        <label>Search</label>
        <div class="search-wrapper">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" class="search-icon">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <input
            type="text"
            v-model="filters.search"
            @input="debounceSearch"
            placeholder="Search by name, phone, or email..."
          />
        </div>
      </div>
    </div>

    <!-- Stats Summary -->
    <div class="stats-summary">
      <div class="summary-card">
        <div class="summary-value">{{ leads.length }}</div>
        <div class="summary-label">Total Leads</div>
      </div>
      <div class="summary-card">
        <div class="summary-value">{{ getLeadsByStatus('new').length }}</div>
        <div class="summary-label">New</div>
      </div>
      <div class="summary-card">
        <div class="summary-value">{{ getLeadsByStatus('contacted').length }}</div>
        <div class="summary-label">Contacted</div>
      </div>
      <div class="summary-card">
        <div class="summary-value">{{ getLeadsByStatus('won').length }}</div>
        <div class="summary-label">Won</div>
      </div>
    </div>

    <!-- Leads Table -->
    <div class="leads-table-container">
      <div v-if="isLoading" class="loading-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" opacity="0.25"/>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round" class="spinner"/>
        </svg>
        <p>Loading leads...</p>
      </div>

      <div v-else-if="filteredLeads.length === 0" class="empty-state">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" opacity="0.2">
          <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>
        <h3>No leads found</h3>
        <p>Try adjusting your filters or check back later</p>
      </div>

      <div v-else class="leads-table">
        <div class="leads-grid">
          <div v-for="lead in filteredLeads" :key="lead.id" class="lead-card">
            <div class="lead-header">
              <div class="lead-avatar">
                {{ getInitials(lead.customer_name) }}
              </div>
              <div class="lead-info">
                <h3>{{ lead.customer_name }}</h3>
                <p class="lead-phone">{{ lead.phone_number }}</p>
              </div>
              <div :class="['lead-status', getStatusClass(lead.status)]">
                {{ formatStatus(lead.status) }}
              </div>
            </div>

            <div class="lead-details">
              <div v-if="lead.email" class="detail-row">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <span>{{ lead.email }}</span>
              </div>

              <div v-if="lead.source" class="detail-row">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>Source: {{ lead.source }}</span>
              </div>

              <div class="detail-row">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                </svg>
                <span>{{ formatDate(lead.created_at) }}</span>
              </div>
            </div>

            <div class="lead-actions">
              <button class="action-btn primary" @click="viewLeadDetails(lead)">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                </svg>
                View Details
              </button>
              <button class="action-btn secondary" @click="contactLead(lead)">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
                Contact
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="filteredLeads.length > 0" class="pagination">
      <button @click="prevPage" :disabled="currentPage === 1" class="page-btn">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
        </svg>
        Previous
      </button>
      <div class="page-info">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <button @click="nextPage" :disabled="currentPage === totalPages" class="page-btn">
        Next
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgentLeadsView',
  data () {
    return {
      leads: [],
      filters: {
        status: '',
        search: ''
      },
      isLoading: false,
      currentPage: 1,
      perPage: 12,
      searchDebounce: null
    }
  },
  computed: {
    filteredLeads () {
      let filtered = [...this.leads]

      // Filter by status
      if (this.filters.status) {
        filtered = filtered.filter(lead => lead.status === this.filters.status)
      }

      // Filter by search
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase()
        filtered = filtered.filter(lead =>
          lead.customer_name?.toLowerCase().includes(search) ||
          lead.phone_number?.toLowerCase().includes(search) ||
          lead.email?.toLowerCase().includes(search)
        )
      }

      // Paginate
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return filtered.slice(start, end)
    },

    totalPages () {
      let filtered = [...this.leads]

      if (this.filters.status) {
        filtered = filtered.filter(lead => lead.status === this.filters.status)
      }

      if (this.filters.search) {
        const search = this.filters.search.toLowerCase()
        filtered = filtered.filter(lead =>
          lead.customer_name?.toLowerCase().includes(search) ||
          lead.phone_number?.toLowerCase().includes(search) ||
          lead.email?.toLowerCase().includes(search)
        )
      }

      return Math.ceil(filtered.length / this.perPage)
    }
  },
  async mounted () {
    // Check authentication
    const agentToken = localStorage.getItem('agent_token')
    if (!agentToken) {
      this.$router.push('/agent-login')
      return
    }

    await this.loadLeads()
  },
  methods: {
    async loadLeads () {
      this.isLoading = true
      // const agentToken = localStorage.getItem('agent_token')

      try {
        // TODO: Replace with actual API endpoint when ready
        // const agentToken = localStorage.getItem('agent_token')
        // const response = await fetch('https://api.quali-d.com/agents/my-leads', {
        //   headers: {
        //     Authorization: `Bearer ${agentToken}`,
        //     Accept: 'application/json'
        //   }
        // })

        // For now, using mock data
        await new Promise(resolve => setTimeout(resolve, 800))

        this.leads = [
          {
            id: 1,
            customer_name: 'Ahmed Mohamed',
            phone_number: '+201234567890',
            email: 'ahmed@example.com',
            status: 'new',
            source: 'website',
            created_at: '2025-11-21 10:30:00'
          },
          {
            id: 2,
            customer_name: 'Sara Ali',
            phone_number: '+201112233445',
            email: 'sara@example.com',
            status: 'contacted',
            source: 'referral',
            created_at: '2025-11-20 15:45:00'
          },
          {
            id: 3,
            customer_name: 'Mohamed Hassan',
            phone_number: '+201555666777',
            email: 'mohamed@example.com',
            status: 'qualified',
            source: 'facebook',
            created_at: '2025-11-19 09:15:00'
          },
          {
            id: 4,
            customer_name: 'Fatma Ibrahim',
            phone_number: '+201999888777',
            email: 'fatma@example.com',
            status: 'proposal',
            source: 'google',
            created_at: '2025-11-18 14:20:00'
          },
          {
            id: 5,
            customer_name: 'Omar Khaled',
            phone_number: '+201333444555',
            email: 'omar@example.com',
            status: 'won',
            source: 'website',
            created_at: '2025-11-17 11:00:00'
          }
        ]
      } catch (error) {
        console.error('Error loading leads:', error)
      } finally {
        this.isLoading = false
      }
    },

    debounceSearch () {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.currentPage = 1
      }, 300)
    },

    getLeadsByStatus (status) {
      return this.leads.filter(lead => lead.status === status)
    },

    getInitials (name) {
      if (!name) return '??'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    getStatusClass (status) {
      const classes = {
        new: 'status-new',
        contacted: 'status-contacted',
        qualified: 'status-qualified',
        proposal: 'status-proposal',
        negotiation: 'status-negotiation',
        won: 'status-won',
        lost: 'status-lost'
      }
      return classes[status] || 'status-default'
    },

    formatStatus (status) {
      return status.charAt(0).toUpperCase() + status.slice(1)
    },

    formatDate (dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays} days ago`

      return date.toLocaleDateString()
    },

    viewLeadDetails (lead) {
      console.log('View lead details:', lead)
      // TODO: Open modal or navigate to detail page
    },

    contactLead (lead) {
      console.log('Contact lead:', lead)
      // TODO: Open contact modal or initiate call/message
    },

    prevPage () {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    nextPage () {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    }
  }
}
</script>

<style scoped>
.agent-leads-view {
  min-height: 100vh;
  background: #f7fafc;
  padding: 24px;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-button {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: white;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  color: #4a5568;
}

.back-button:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 4px 0;
}

.subtitle {
  font-size: 14px;
  color: #718096;
  margin: 0;
}

.refresh-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-button:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.refresh-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.refresh-button svg.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Filters */
.filters-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filter-group {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group label {
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
}

.filter-group select,
.search-wrapper input {
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  background: white;
  color: #2d3748;
}

.filter-group select:focus,
.search-wrapper input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  pointer-events: none;
}

.search-wrapper input {
  padding-left: 40px;
  width: 100%;
}

/* Stats Summary */
.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.summary-value {
  font-size: 32px;
  font-weight: 700;
  color: #667eea;
  line-height: 1;
  margin-bottom: 8px;
}

.summary-label {
  font-size: 13px;
  color: #718096;
  font-weight: 500;
}

/* Leads Table */
.leads-table-container {
  background: white;
  border-radius: 12px;
  padding: 24px;
  min-height: 400px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  color: #a0aec0;
}

.loading-state svg {
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-state p,
.empty-state p {
  margin: 8px 0 0 0;
  font-size: 14px;
}

.empty-state h3 {
  margin: 16px 0 4px 0;
  color: #4a5568;
  font-size: 18px;
}

.leads-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
}

.lead-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s;
  background: white;
}

.lead-card:hover {
  border-color: #cbd5e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.lead-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.lead-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  flex-shrink: 0;
}

.lead-info {
  flex: 1;
  min-width: 0;
}

.lead-info h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 4px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-phone {
  font-size: 13px;
  color: #718096;
  margin: 0;
}

.lead-status {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.status-new {
  background: #EBF4FF;
  color: #2563EB;
}

.status-contacted {
  background: #F0FDF4;
  color: #16A34A;
}

.status-qualified {
  background: #FEF3C7;
  color: #D97706;
}

.status-proposal {
  background: #F3E8FF;
  color: #9333EA;
}

.status-negotiation {
  background: #FEE2E2;
  color: #DC2626;
}

.status-won {
  background: #D1FAE5;
  color: #059669;
}

.status-lost {
  background: #F3F4F6;
  color: #6B7280;
}

.lead-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #4a5568;
}

.detail-row svg {
  color: #a0aec0;
  flex-shrink: 0;
}

.lead-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.action-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.action-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.action-btn.secondary {
  background: white;
  color: #667eea;
  border: 1px solid #667eea;
}

.action-btn.secondary:hover {
  background: #f7fafc;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}

.page-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #4a5568;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .agent-leads-view {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .leads-grid {
    grid-template-columns: 1fr;
  }

  .filters-section {
    flex-direction: column;
  }

  .filter-group {
    min-width: 100%;
  }
}
</style>
